/**
 * MIC-2/3 — search + qty entry; unknown SKU → new-item proposal composer.
 * Uncounted float to top; counted sink to bottom (§2).
 * Paginated 10/20/30 (dev-sync 4a296703).
 */
import { useEffect, useMemo, useState } from "react";
import type { MfgInventoryCountLine } from "@/lib/manufacturing/types/inventoryCount";
import { prepareInventoryCountLinesForDisplay } from "@/lib/manufacturing/inventoryCountLineDisplay";
import {
  ghostBtnClass,
  inputClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingInventoryCountNewItemComposer } from "./ManufacturingInventoryCountNewItemComposer";
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";

type Props = {
  lines: MfgInventoryCountLine[];
  busy: boolean;
  sessionOpen: boolean;
  savingLineId: string | null;
  proposing: boolean;
  onCommitQty: (lineId: string, countedQty: number | null) => void;
  onProposeNewItem: (opts: {
    proposed_name: string;
    proposed_unit: string;
    proposed_category: string;
    proposed_qty: number | null;
  }) => void;
};

export function ManufacturingInventoryCountLinesPanel({
  lines,
  busy,
  sessionOpen,
  savingLineId,
  proposing,
  onCommitQty,
  onProposeNewItem,
}: Props) {
  const [search, setSearch] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const display = useMemo(
    () => prepareInventoryCountLinesForDisplay(lines, search),
    [lines, search],
  );
  const pager = usePagination(display, 10);
  const uncountedCount = lines.filter((l) => l.counted_qty == null).length;
  const countedCount = lines.length - uncountedCount;
  const searchTrim = search.trim();
  const noMatches = searchTrim.length > 0 && display.length === 0;

  useEffect(() => {
    pager.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset page on search/filter
  }, [search, display.length]);

  if (busy && lines.length === 0 && !composerOpen) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">Loading lines…</p>;
  }

  return (
    <div className="space-y-3">
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Search materials
        <input
          className={`${inputClass} mt-1`}
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setComposerOpen(false);
          }}
          placeholder="Name or SKU…"
          autoComplete="off"
          autoFocus
        />
      </label>
      <p className="text-[11px] text-gray-500 dark:text-zinc-400">
        {uncountedCount} remaining · {countedCount} counted
        {searchTrim ? ` · ${display.length} match${display.length === 1 ? "" : "es"}` : ""}
        . Uncounted stay on top; counted sink after you save a qty.
      </p>

      {lines.length === 0 && !searchTrim ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">
          No materials on this plant yet. Search for a name to propose a new item, or add materials
          in Inventory first.
        </p>
      ) : null}

      <PaginationBar
        pageIndex={pager.pageIndex}
        pageCount={pager.pageCount}
        pageSize={pager.pageSize}
        totalCount={pager.totalCount}
        firstShown={pager.firstShown}
        lastShown={pager.lastShown}
        onPageChange={pager.setPageIndex}
        onPageSizeChange={pager.setPageSize}
        noun="materials"
        alwaysShow={display.length > 0}
      />

      <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
        {pager.paginatedItems.map((line) => (
          <CountLineRow
            key={line.id}
            line={line}
            disabled={!sessionOpen || busy}
            saving={savingLineId === line.id}
            onCommitQty={onCommitQty}
          />
        ))}
      </ul>

      {display.length > 10 ? (
        <PaginationBar
          pageIndex={pager.pageIndex}
          pageCount={pager.pageCount}
          pageSize={pager.pageSize}
          totalCount={pager.totalCount}
          firstShown={pager.firstShown}
          lastShown={pager.lastShown}
          onPageChange={pager.setPageIndex}
          onPageSizeChange={pager.setPageSize}
          noun="materials"
          alwaysShow
        />
      ) : null}

      {noMatches || (lines.length === 0 && searchTrim) ? (
        <div className="space-y-2">
          <p className="text-sm text-gray-600 dark:text-zinc-300">
            No materials match &ldquo;{searchTrim}&rdquo;.
          </p>
          {sessionOpen && !composerOpen ? (
            <button
              type="button"
              className={ghostBtnClass}
              disabled={busy || proposing}
              onClick={() => setComposerOpen(true)}
            >
              + Propose new item
            </button>
          ) : null}
          {composerOpen ? (
            <ManufacturingInventoryCountNewItemComposer
              key={searchTrim}
              suggestedName={searchTrim}
              disabled={!sessionOpen}
              busy={proposing}
              onCancel={() => setComposerOpen(false)}
              onSubmit={(opts) => {
                onProposeNewItem(opts);
                setComposerOpen(false);
                setSearch("");
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CountLineRow({
  line,
  disabled,
  saving,
  onCommitQty,
}: {
  line: MfgInventoryCountLine;
  disabled: boolean;
  saving: boolean;
  onCommitQty: (lineId: string, countedQty: number | null) => void;
}) {
  const [draft, setDraft] = useState(line.counted_qty == null ? "" : String(line.counted_qty));
  useEffect(() => {
    setDraft(line.counted_qty == null ? "" : String(line.counted_qty));
  }, [line.id, line.counted_qty]);

  const isCounted = line.counted_qty != null;

  function commit() {
    const trimmed = draft.trim();
    if (trimmed === "") {
      if (line.counted_qty != null) onCommitQty(line.id, null);
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) return;
    if (line.counted_qty === n) return;
    onCommitQty(line.id, n);
  }

  return (
    <li
      className={`flex flex-wrap items-center gap-3 py-2.5 text-sm ${isCounted ? "opacity-80" : ""}`}
    >
      <div className="min-w-0 flex-1">
        <div className="font-medium text-gray-900 dark:text-zinc-50">
          {line.material_name ?? "Material"}
        </div>
        <div className="font-mono text-xs text-gray-500 dark:text-zinc-400">
          {line.material_code ?? line.material_id.slice(0, 8)}
          {" · expected "}
          {line.expected_qty}
          {line.material_unit ? ` ${line.material_unit}` : ""}
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-300">
        Qty
        <input
          className={`${inputClass} w-24`}
          inputMode="decimal"
          value={draft}
          disabled={disabled || saving}
          placeholder="—"
          aria-label={`Counted quantity for ${line.material_name ?? line.material_code ?? "material"}`}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      </label>
    </li>
  );
}
